import classNames from 'classnames';
import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';

import Button from '../../../components/buttons/Button';
import { UserAddressIcon } from '../../../components/SVGIcons/UserAddressIcon';
import { UserIdIcon } from '../../../components/SVGIcons/UserIdIcon';
import type { KycInitiationTrigger } from '../../../hooks/kyc/types';
import { useKycImpressionReport } from '../../../hooks/kyc/useKycImpressionReport';
import { EnumKycStatus } from '../../../services/types';
import type { RootStore } from '../../../stores/RootStore';
import { EnumOpenDialog } from '../../../stores/ui/types';
import { useStores } from '../../../stores/useStores';

import styles from './PlayerKycExplanation.module.scss';

export const PlayerKycExplanation = observer(function PlayerKycExplanation(props: {
  isMountedButHidden?: boolean;
  trigger: KycInitiationTrigger;
  onStartClick: VoidFunction;
  className?: string;
  onBeforePersonalDetailsOpened?: (stores: RootStore) => void;
  onAfterPersonalDetailsClosed?: (stores: RootStore) => void;
}) {
  const stores = useStores();
  const { uiStore, userStore } = stores;
  const { t } = useTranslation();

  useKycImpressionReport(props.trigger, props.isMountedButHidden);

  const handleStartClick = () => {
    if (!userStore.profile?.address && !userStore.profile?.city && !userStore.profile?.cleanGeoData?.territoryCode) {
      if (typeof props.onBeforePersonalDetailsOpened === 'function') {
        props.onBeforePersonalDetailsOpened(stores);
      }

      uiStore.setOpenDialog({
        dialog: EnumOpenDialog.isOpenPersonalDetailsDialog,
        options: { onClose: () => props.onAfterPersonalDetailsClosed?.(stores) },
      });
    } else {
      props.onStartClick();
    }
  };

  const residenceAddress = {
    address: userStore.profile?.address ? `${userStore.profile?.address},` : '',
    city: userStore.profile?.city ? `${userStore.profile?.city},` : '',
    state: userStore.profile?.cleanGeoData?.territoryCode ? `${userStore.profile.cleanGeoData.territoryCode}` : '',
  };

  const residenceAddressStr = `${residenceAddress.address} ${residenceAddress.city} ${residenceAddress.state}`;

  const isExpired = userStore.profile?.kycStatus === EnumKycStatus.expired;
  const titleKey = isExpired ? 'kyc.finishVerification' : 'kyc.updateVerification';
  const buttonTextKey = isExpired ? 'kyc.letsGo' : 'kyc.getStarted';

  return (
    <div className={classNames(styles.wrapper, props.className)}>
      <div className={styles.name}>
        {userStore.profile?.firstName} {userStore.profile?.lastName}
      </div>
      <div className={styles.address}>{residenceAddressStr}</div>
      <div className={styles.title}>{t(titleKey)}</div>
      <div className={styles.whatYouWillNeed}>{t('kyc.whatYoullNeed')}</div>
      <div className={styles.mainBlock}>
        <div className={classNames(styles.requirementsBlock, { [styles.singleChild]: isExpired })}>
          <div>
            <UserIdIcon />
            <div>{t('kyc.governmentId')}</div>
          </div>
          {!isExpired && (
            <div>
              <UserAddressIcon />
              <div>{t('kyc.proofOfAddress')}</div>
            </div>
          )}
        </div>
        <Button btnType="main" classNameWrapper={styles.startButton} handleOnClick={handleStartClick}>
          {t(buttonTextKey)}
        </Button>
      </div>
    </div>
  );
});
